import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from "axios";
import { tokenService } from "@/src/services/auth/token.service";
import { useErrorStore } from "@/src/services/stores/error.store";
import { useRequestStore } from "@/src/services/stores/request.store";
import { ApiError, ApiResponse } from "@/src/types/api.types";
import { ErrorType } from "@/src/types/error.type";

console.log("API Interceptor loaded", new Date().toISOString());

declare global {
  interface Window {
    refreshTimeOut?: ReturnType<typeof setTimeout>;
  }
}

export const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL?.trim() || "/api",
});

const urls = ["/auth/login", "/auth/refresh"];

const formatApiError = (error: unknown, statusCode: number): ApiError => {
  const typedError = error as ErrorType;
  if (typedError?.code && typedError?.message) {
    return {
      code: typedError.code,
      message: typedError.message,
      statusCode,
      timestamp: new Date().toISOString(),
      details: typedError.details,
    };
  }

  const messageMap: Record<number, string> = {
    400: "Bad Request",
    401: "Unauthorized",
    403: "Forbidden",
    404: "Not Found",
    409: "Conflict",
    422: "Validation Failed",
    429: "Too Many Requests",
    500: "Internal Server Error",
    502: "Bad Gateway",
    503: "Service Unavailable",
  };

  return {
    code: `ERROR_${statusCode}`,
    message: messageMap[statusCode] || "An error occurred",
    statusCode,
    timestamp: new Date().toISOString(),
    details:
      typeof error === "object" && error !== null
        ? (error as Record<string, unknown>)
        : { raw: String(error) },
  };
};

const doRefresh = () => {
  const accessToken = tokenService.getToken() ?? "";

  return api
    .post(
      `/auth/refresh`,
      {},
      {
        headers: {
          Authorization: "Bearer " + accessToken,
        },
        withCredentials: true,
      }
    )
    .catch(() => {
      clearTimeout(window.refreshTimeOut);
    });
};

const process203 = (response: AxiosResponse) => {
  const authData = urls.includes(response.config.url as string)
    ? response.data.data
    : [202, 203].includes(response?.status)
      ? response.data.data.auth
      : undefined;

  if (!authData) return;
  if (!authData.token) return;

  tokenService.setToken(authData.token);

  console.log("Process203 refreshTimeout: ", window.refreshTimeOut);
  if (window.refreshTimeOut && response.status != 203) return;

  clearTimeout(window.refreshTimeOut);

  console.log(
    "setTimeout: ",
    new Date(Date.now() + authData.re).toLocaleString("sv-SE")
  );

  window.refreshTimeOut = setTimeout(() => {
    console.log("refresh timer fired");
    window.refreshTimeOut = undefined;
    doRefresh();
  }, authData.re);
};

// Request interceptor - add token and set loading state
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenService.getToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Set loading state based on request key
    const requestKey = `${config.method?.toUpperCase()}_${config.url}`;
    const requestStore = useRequestStore.getState();
    requestStore.setLoading(requestKey, true);

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle success, errors, and clear loading state
api.interceptors.response.use(
  async (response: AxiosResponse) => {
    console.log("interceptor response:", response.config.url);

    // Clear loading state
    const requestKey = `${response.config.method?.toUpperCase()}_${response.config.url}`;
    const requestStore = useRequestStore.getState();
    requestStore.setLoading(requestKey, false);

    // Clear any errors for this request
    const errorStore = useErrorStore.getState();
    errorStore.clearError(requestKey);

    process203(response);

    return response;
  },
  async (error: AxiosError) => {
    const logStatus = error.response?.status ?? error.code ?? "unknown";
    if (process.env.NODE_ENV === "development") {
      console.warn("interceptor error:", error.config?.url, logStatus, error.message);
    }

    // Clear loading state
    const requestKey = `${error.config?.method?.toUpperCase()}_${error.config?.url}`;
    const requestStore = useRequestStore.getState();
    requestStore.setLoading(requestKey, false);

    // Handle error response
    const response = error.response as AxiosResponse<ApiResponse> | undefined;
    const statusCode = response?.status ?? 500;

    let apiError: ApiError;

    if (response?.data?.error) {
      apiError = response.data.error;
    } else if (response?.data) {
      apiError = formatApiError(response.data, statusCode);
    } else {
      apiError = formatApiError(
        {
          message: error.message,
          code: error.code,
        },
        statusCode
      );
    }

    // Store error in Zustand
    const errorStore = useErrorStore.getState();
    errorStore.setError(requestKey, apiError);

    // Set global error for critical errors (5xx, timeout)
    if (statusCode >= 500 || statusCode === 0) {
      errorStore.setGlobalError(apiError);
    }

    // Handle 401 - redirect to login
    if (statusCode === 401) {
      tokenService.clearToken();
      if (typeof window !== "undefined") {
        window.location.href = "/login";
      }
    }

    // Return the error response for component-level handling
    return Promise.reject({
      error: apiError,
      response,
    });
  }
);
